import { useCallback, useEffect, useRef, useState } from 'react';
import { mergeMedia } from '../utils/galleryMedia';

export default function useSyncedImages(folder, fallback = []) {
  return useGalleryFeed(folder, fallback).items;
}
export function useGalleryFeed(folder, fallback = [], options = {}) {
  const [remote, setRemote] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const controllers = useRef(new Set());
  const endpoint = options.endpoint || '/api/synced-images';
  const requestPage = useCallback(async nextCursor => {
    if (!folder || inFlight.current) return;
    inFlight.current = true;
    const current = generation.current;
    const controller = new AbortController();
    controllers.current.add(controller);
    setLoading(true); setError(false);
    const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
    const base = isLocal ? 'http://127.0.0.1:4000' : '';
    try {
      const response = await fetch(`${base}${endpoint}?folder=${encodeURIComponent(folder)}&page=true&limit=24${nextCursor ? `&cursor=${encodeURIComponent(nextCursor)}` : ''}`, { signal: controller.signal });
      if (!response.ok) throw new Error('gallery_unavailable');
      const data = await response.json();
      const items = Array.isArray(data) ? data : data.items;
      if (!Array.isArray(items)) throw new Error('invalid_gallery_response');
      if (!controller.signal.aborted && current === generation.current) {
        setRemote(previous => nextCursor ? mergeMedia(previous, items) : items);
        setCursor(Array.isArray(data) ? null : data.nextCursor || null);
      }
    } catch {
      if (!controller.signal.aborted && current === generation.current) setError(true);
    } finally {
      controllers.current.delete(controller);
      if (current === generation.current) { inFlight.current = false; setLoading(false); }
    }
  }, [folder, endpoint]);
  useEffect(() => {
    generation.current++; inFlight.current = false;
    setRemote([]); setCursor(null); setError(false); setLoading(false);
    const active = controllers.current;
    requestPage(null);
    return () => { generation.current++; for (const controller of active) controller.abort(); active.clear(); };
  }, [requestPage]);
  const showLocal = options.appendFallback === false ? error && !remote.length : !cursor || error;
  const local = showLocal ? fallback : [];
  return { items: mergeMedia(remote, local), hasMore: !!cursor, loading, error, loadMore: () => requestPage(cursor) };
}
